/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC} from 'react';
import {
  IconButton,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import classnames from 'classnames';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';

// defines the styles used by the component
const useStyles = makeStyles((theme: Theme) => ({
  leftBorderRadius : {
    borderRadius : toggleThemeStyle('50% 0 0 50%', '20px 0 0 20px')
  },
  rightBorderRadius : {
    borderRadius : toggleThemeStyle('0 50% 50% 0', '0 20px 20px 0')
  },
  buttonContainer : {
    width          : '100%',
    height         : 'auto',
    display        : 'flex',
    position       : 'relative',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  button : {
    width        : toggleThemeStyle(36, 40),
    height       : toggleThemeStyle(36, 40),
    padding      : 0,
    borderRadius : '20px'
  },
  buttonText : {
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    textAlign     : 'center',
    fontWeight    : ({fromDateTime}: {fromDateTime: boolean}) => fromDateTime ? 400 : 'bold',
    lineHeight    : '24px',
    letterSpacing : toggleThemeStyle('1px', '0.44px'),
    verticalAlign : 'middle'
  },
  outlined : {
    border : toggleThemeStyle(`1px solid ${theme.palette.primary.dark}`, `1px solid ${dlsColor('darkTeal')}`)
  },
  filled : {
    '&:hover' : {
      backgroundColor : toggleThemeStyle(dlsColor('skyBlue'), dlsColor('paleBlue2'))
    },
    backgroundColor : ({fromDateTime}: {fromDateTime: boolean}) => {
      const baseColor = fromDateTime ? dlsColor('darkTeal') : dlsColor('caBlue');
      return toggleThemeStyle(baseColor, dlsColor('paleBlue'))();
    }
  },
  highlighted : {
    backgroundColor : toggleThemeStyle('rgba(157, 232, 245, 0.3)', dlsColor('paleBlue'))
  },
  contrast : {
    color : toggleThemeStyle(theme.palette.primary.contrastText, dlsColor('darkTeal'))
  },
  noVisibility : {
    visibility : 'hidden'
  }
}));

// defines the prop definitions for the component
export interface DayProps {
  filled?: boolean;
  outlined?: boolean;
  highlighted?: boolean;
  disabled?: boolean;
  startOfRange?: boolean;
  endOfRange?: boolean;
  onClick?: () => void;
  onHover?: () => void;
  value: number | string;
  fromDateTime?: boolean;
  isCurrentTheme?: boolean;
  noVisibilityOnDisabled?: boolean;
}

/**
 * @Day
 *
 * Defines the Day component.
 */
export const Day: FC<DayProps> = ({
  startOfRange,
  endOfRange,
  disabled,
  highlighted,
  outlined,
  filled,
  onClick,
  onHover,
  value,
  fromDateTime = false,
  noVisibilityOnDisabled = true
}: DayProps) => {
  const classes = useStyles({fromDateTime});

  return (
    <div className={classnames(
      (disabled && fromDateTime && noVisibilityOnDisabled) && classes.noVisibility,
      classes.buttonContainer,
      startOfRange && classes.leftBorderRadius,
      endOfRange && classes.rightBorderRadius,
      !disabled && highlighted && classes.highlighted
    )}>
      <IconButton disabled={disabled}
        onClick={onClick}
        onMouseOver={onHover}
        className={classnames(
          classes.button,
          !disabled && outlined && classes.outlined,
          !disabled && filled && classes.filled
        )}>
        <Typography color={!disabled ? 'textPrimary' : 'textSecondary'}
          className={classnames(
            classes.buttonText,
            !disabled && filled && classes.contrast
          )}
          variant='body2'>
          {value}
        </Typography>
      </IconButton>
    </div>
  );
};

export default Day;
